<!--
 * @Author: YuanYuan
 * @Date: 2024-02-04 18:47:53
 * @LastEditTime: 2025-12-15 10:27:58
 * @description: 带分页的表格
-->
<template>
  <div v-loading="wholeLoading">
    <el-table
      ref="paginatedTableRef"
      :data="tableShowData"
      :cell-style="topAlign && {'vertical-align': 'top'}"
      @selection-change="checkedChange"
      :row-key="rowKey"
      v-bind="tableAttrs"
      v-on="tableListeners"
      :size="tableSize"
      @row-click="onRowClick"
      @sort-change="sortHandler">
      <el-table-column v-if="radio" width="50">
        <template slot-scope="{row}">
          <el-radio v-model="radioChecked" :label="row[rowKey]" class="relative-top-2">{{''}}</el-radio>
        </template>
      </el-table-column>
      <el-table-column v-if="selection" :selectable="tableAttrs.selectable" type="selection" />
      <el-table-column
        v-if="showIndex"
        :label="$t('common.serialNo')"
        type="index"
        align="center"
        :width="indexWidth"
        :index="index => rowIndex(index)" />
      <slot></slot>
    </el-table>
    <el-pagination
      v-bind="paginationAttrs"
      class="py-12"
      :total="total"
      :select-count="selection?checkedList.length:false"
      :page-sizes="pageSizes||$const['Common/PageSizes']"
      :current-page.sync="pageIndex"
      :page-size="pageSize"
      :thousand-total="true"
      @size-change="pageSizeHandler"
      @current-change="pageNoHandler">
    </el-pagination>
  </div>
</template>

<script setup>
import { toRefs, watch, ref, computed, getCurrentInstance } from 'vue'
import { pick } from 'lodash-es'

const emit = defineEmits(['checked-change', 'radio-change'])

const $vm = getCurrentInstance().proxy
const { $api, $refs } = $vm

const props = defineProps({
  // 只有在apiPath为空时，才有效
  data: {
    type: Array,
    default: () => ([])
  },
  // 表格数据为请求的 api 数据
  apiPath: String,
  // true: 前端分页
  isStatic: {
    type: Boolean,
    default: false
  },
  // 是否可勾选
  selection: {
    type: Boolean,
    default: false
  },
  radio: {
    type: Boolean,
    default: false
  },
  defaultRadioValue: {
    type: String,
    default: ''
  },
  // 分页器可选项
  pageSizes: Array,
  // 是否显示序号
  showIndex: {
    type: Boolean,
    default: false
  },
  topAlign: {
    type: Boolean,
    default: false
  },
  // 返回 {tableList: [], total: xx}
  responseFunc: Function,
  rowKey: {
    type: String,
    default: 'id'
  },
  // 后端过滤查询参数
  queryParams: {
    type: Object,
    default: () => ({})
  },
  autoLoad: {
    type: Boolean,
    default: true
  },
  tableSize: {
    type: String,
    default: ''
  },
  successCode: {
    type: [Number, String],
    default: 200
  },
  defaultSortKey: {
    type: String,
    default: 'updateTime'
  },
  sortFunc: Function
})

const {
  isStatic, pageSizes, apiPath, responseFunc, rowKey,
  queryParams, data, radio, autoLoad, defaultRadioValue,
  tableSize, successCode, defaultSortKey, sortFunc
} = toRefs(props)
 // 远程表格总数
const backendTotal = ref(0)
const staticTableDatabase = ref([])
const tableShowData = ref([]) // 表格当前显示页数据
 // 静态表格过滤后所有数据
const filteredData = ref([])
const pageIndex = ref(1)
const pageSize = ref(10)
const checkedList = ref([])
const wholeLoading = ref(false)
const radioChecked = ref(defaultRadioValue.value)
const tableAttrs = pick($vm.$attrs, 'border', 'cell-style', 'loading', 'height', 'selectable')
const tableListeners = pick($vm.$listeners,  'cell-click', 'cell-dblclick', 'row-dblclick', 'header-click', 'header-dblclick', 'filter-change')
const paginationAttrs = pick($vm.$attrs, 'layout')

// 静态表格总数为过滤后的数据总数，远程表格总数由后端返回
const total = computed(() => {
  if (apiPath.value) {
    if (isStatic.value) return filteredData.value.length
    return backendTotal.value
  } else {
    return data.value.length || 0
  }
})

const indexWidth = computed(() => {
  const maxTotalLen = String(total.value).length
  return maxTotalLen > 7 ? 100 : maxTotalLen > 5 ? 80 : 70
})

// 设置前端分页表格当前页显示数据
const setStaticTableData = () => {
  const start = (pageIndex.value - 1) * pageSize.value
  const end = start + pageSize.value
  const range = filteredData.value.slice(start, end)
  if (range.length) {
    tableShowData.value = range
  } else {
    if (pageIndex.value > 1) {
      --pageIndex.value
      setStaticTableData()
    } else {
      tableShowData.value = []
    }
  }
}

// 前端获取表格全量数据
const getStaticDatabase = () => {
  wholeLoading.value = true
  const params = {
    ...queryParams.value
  }
  $api[apiPath.value](params).then(res => {
    if (res?.code === successCode.value) {
      if (responseFunc.value) {
        staticTableDatabase.value = responseFunc.value(res).tableList
      } else {
        staticTableDatabase.value = res?.data || []
      }
      filteredData.value = staticTableDatabase.value.slice()
      setStaticTableData()
    }
  }).finally(() => (wholeLoading.value = false))
}

// 后端搜索获取当前页表格数据
const getRemoteData = () => {
  wholeLoading.value = true
  const params = {
    limit: pageSize.value,
    page: pageIndex.value,
    ...queryParams.value
  }
  $api[apiPath.value](params).then(res=> {
    if (res?.code === successCode.value) {
      if (responseFunc.value) {
        const { tableList, total } = responseFunc.value(res)
        tableShowData.value = tableList
        backendTotal.value = total
      } else {
        tableShowData.value = res?.data || []
        backendTotal.value = res?.count * 1 || 0
      }
    }
  }).finally(() => (wholeLoading.value = false))
}

const getTableData = () => {
  if (apiPath.value) {
    if (isStatic.value) {
      getStaticDatabase()
    } else {
      getRemoteData()
    }
  } else {
    staticTableDatabase.value = data.value || []
    filteredData.value = staticTableDatabase.value.slice()
    setStaticTableData()
  }
}

const startQuery = () => {
  $vm.$nextTick(() => {
    pageIndex.value = 1
    getTableData()
  })
}

const sortTypeFunc = (a, b, prop, order) => {
  const type = typeof a[prop]
  if (order === 'ascending') {
    switch (type) {
      case 'string':
        return a[prop].localeCompare(b[prop])
      case 'number':
        return a[prop] - b[prop]
      default:
      return sortTypeFunc(a, b, defaultSortKey.value, 'ascending')
    }
  } else if (order === 'descending') {
    switch (type) {
      case 'string':
        return b[prop].localeCompare(a[prop])
      case 'number':
        return b[prop] - a[prop]
      default:
        return sortTypeFunc(a, b, defaultSortKey.value, 'descending')
    }
  }
}

const sortHandler = data => {
  const { order, prop } = data
  if (isStatic.value) {
    // coming
  } else {
    if (sortFunc.value) {
      sortFunc.value(data)
    } else {
      if (order) {
        tableShowData.value = tableShowData.value.sort((a, b) => sortTypeFunc(a, b, prop, order))
      } else {
        tableShowData.value = tableShowData.value.sort((a, b) => sortTypeFunc(a, b, defaultSortKey.value, 'descending'))
      }
    }
  }
}

// 初始化或静态表格数据源发生变化时触发
watch(() => apiPath.value, () => {
  autoLoad.value && startQuery()
}, { deep: true, immediate: true })

watch(() => data.value, () => {
  (!apiPath.value && autoLoad.value) && startQuery()
}, { deep: true, immediate: true })

watch(() => radioChecked.value, () => {
  emit('radio-change', radioChecked.value)
})

const onRowClick = row => {
  radioChecked.value = row[rowKey.value]
}

const pageSizeHandler = size => {
  pageSize.value = size
  pageIndex.value = 1
  if (apiPath.value && !isStatic.value) {
    getRemoteData()
  } else {
    setStaticTableData()
  }
}

const pageNoHandler = () => {
  if (apiPath.value && !isStatic.value) {
    getRemoteData()
  } else {
    setStaticTableData()
  }
}

const rowIndex = idx => {
  return (pageIndex.value - 1) * pageSize.value + idx + 1
}

const checkedChange = arr => {
  checkedList.value = arr
  emit('checked-change', checkedList.value)
}

const initTable = () => {
  radioChecked.value = defaultRadioValue.value
  $refs.paginatedTableRef.clearSelection()
  $refs.paginatedTableRef.clearSort()
  $refs.paginatedTableRef.clearFilter()
  startQuery()
}

defineExpose({ startQuery, initTable })
</script>
